import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "@shared/lib/http/axiosBaseQuery";
import { stableSerialize } from "@shared/lib/http/stableSerialize";
import { Item, ItemAdd, ItemEdit, ItemSchema, ItemsPaginatedSchema, SearchItemsBrowseResponse } from "@models/item";
import {
  getItemSearchBody,
  ITEM_BROWSE_INCLUDES,
  ITEM_BROWSE_PER_PAGE,
  ItemSearchParams,
} from "./itemsBrowse.utils";
import { seedDetailCacheFromBrowse } from "@features/tickets/shared/utils/seedDetailCacheFromBrowse";


export const itemsApi = createApi({
  reducerPath: "itemsApi",
  baseQuery: axiosBaseQuery({ baseUrl: "/api/" }),
  tagTypes: ["items", "itemsBrowse", "item"],
  endpoints: (b) => ({

    searchItemsBrowse: b.query<SearchItemsBrowseResponse, ItemSearchParams>({
      query: searchItemsBrowseQuery,
      serializeQueryArgs: searchItemsBrowseSerializeQueryArgs,
      transformResponse: parseItemsBrowseResponse,
      providesTags: [{ type: "itemsBrowse", id: "LIST" }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        seedDetailCacheFromBrowse(itemsApi, "getItemById", data.data, dispatch);
      },
    }),

    getItemById: b.query<Item, string | number>({
      query: getItemByIdQuery,
      transformResponse: parseItemResponse,
      providesTags: getItemByIdProvidesTags,
    }),

    addItem: b.mutation<Item, ItemAdd>({
      query: addItemQuery,
      transformResponse: parseItemResponse,
      invalidatesTags: addItemInvalidatesTags,
    }),

    saveItem: b.mutation<Item, { itemId: number; itemToSave: ItemEdit }>({
      query: saveItemQuery,
      transformResponse: parseItemResponse,
      invalidatesTags: saveItemInvalidatesTags,
    }),

    deleteItem: b.mutation<void, number>({
      query: deleteItemQuery,
      invalidatesTags: deleteItemInvalidatesTags,
    }),
    
    getNextItemNumber: b.query<number, void>({
      query: getNextItemNumberQuery,
      transformResponse: getNextItemNumberTransformResponse,
    }),
  }),
});

export const {
  useSearchItemsBrowseQuery,
  useLazySearchItemsBrowseQuery,
  useGetItemByIdQuery,
  useAddItemMutation,
  useSaveItemMutation,
  useDeleteItemMutation,
  useLazyGetNextItemNumberQuery,
} = itemsApi;






//         *******************************************
//         *             helper functions            *
//         *******************************************


/**
 * RTK Query cache tags to invalidate after item create, update, or delete.
 * @param itemId number | undefined - Specific item id when invalidating a single record.
 * @returns Array - Cache tag descriptors for items list, browse list, and optional detail.
 */
function itemMutationTags(itemId?: number) {

  return [
    { type: "items" as const, id: "LIST" as const },
    { type: "itemsBrowse" as const, id: "LIST" as const },
    ...(itemId ? [{ type: "item" as const, id: itemId }] : []),
  ];
}

/**
 * Validates a single item API payload with Zod.
 * @param response unknown - Raw response body from axiosBaseQuery.
 * @returns Item - Parsed item entity.
 */
function parseItemResponse(response: unknown): Item {

  return ItemSchema.parse(response);
}

/**
 * Validates a paginated items browse payload and returns the data array.
 * @param response unknown - Raw response body from axiosBaseQuery.
 * @returns SearchItemsBrowseResponse - Parsed paginated items payload with rows and cursor meta.
 */
function parseItemsBrowseResponse(response: unknown): SearchItemsBrowseResponse {

  return ItemsPaginatedSchema.parse(response);
}

/**
 * Builds a stable cache key for items browse queries with object args.
 * @param endpointName string - RTK Query endpoint name.
 * @param queryArgs ItemSearchParams - Browse filters, includes, sort, and pagination.
 * @returns string - Serialized cache key for the query args.
 */
function searchItemsBrowseSerializeQueryArgs({ endpointName, queryArgs }: { endpointName: string; queryArgs: ItemSearchParams }): string {
  const { useCache: _omit, ...cacheKeyArgs } = queryArgs;

  return `${endpointName}:${stableSerialize(cacheKeyArgs)}`;
}

/**
 * Builds the axios request for items browse search.
 * @param params ItemSearchParams - Browse filters, includes, sort, and pagination.
 * @returns object - URL, method, and POST body for items/search.
 */
function searchItemsBrowseQuery({ include, per_page, useCache, ...params }: ItemSearchParams) {

  return {
    url: "items/search",
    method: "POST" as const,
    // Apply browse defaults and normalize filter for Spatie search API
    data: getItemSearchBody({
      include: include ?? ITEM_BROWSE_INCLUDES,
      per_page: per_page ?? ITEM_BROWSE_PER_PAGE,
      useCache,
      ...params,
    }),
  };
}

/**
 * Builds the axios request to fetch a single item by id.
 * @param itemId string | number - Item primary key.
 * @returns object - URL and method for GET items/:id.
 */
function getItemByIdQuery(itemId: string | number) {

  return {
    url: `items/${itemId}`,
    method: "GET" as const,
    params: {
      include: "customData",
    },
  };
}

/**
 * Resolves RTK Query cache tags for a single item detail query.
 * @param _res Item | undefined - Successful query result (unused).
 * @param _err unknown - Query error (unused).
 * @param itemId string | number - Item primary key from query args.
 * @returns Array - Cache tag for the item detail entity.
 */
function getItemByIdProvidesTags(_res: Item | undefined, _err: unknown, itemId: string | number) {

  return [{ type: "item" as const, id: Number(itemId) }];
}

/**
 * Builds the axios request to create an item.
 * @param itemToAdd ItemAdd - Payload for the new item.
 * @returns object - URL, method, and POST body for items.
 */
function addItemQuery(itemToAdd: ItemAdd) {

  return {
    url: "items",
    method: "POST" as const,
    data: itemToAdd,
  };
}

/**
 * Resolves RTK Query cache tags to invalidate after creating an item.
 * @returns Array - Cache tags for items list and browse list.
 */
function addItemInvalidatesTags() {

  return itemMutationTags();
}

/**
 * Builds the axios request to update an item.
 * @param arg object - Item id and PATCH payload.
 * @returns object - URL, method, and PATCH body for items/:id.
 */
function saveItemQuery({ itemId, itemToSave }: { itemId: number; itemToSave: ItemEdit }) {

  return {
    url: `items/${itemId}`,
    method: "PATCH" as const,
    data: itemToSave,
  };
}

/**
 * Resolves RTK Query cache tags to invalidate after updating an item.
 * @param _res Item | undefined - Successful mutation result (unused).
 * @param _err unknown - Mutation error (unused).
 * @param arg object - Mutation arg containing itemId.
 * @returns Array - Cache tags for lists and the updated item detail.
 */
function saveItemInvalidatesTags(_res: Item | undefined, _err: unknown, { itemId }: { itemId: number; itemToSave: ItemEdit }) {

  return itemMutationTags(itemId);
}

/**
 * Builds the axios request to delete an item.
 * @param itemId number - Item primary key.
 * @returns object - URL and method for DELETE items/:id.
 */
function deleteItemQuery(itemId: number) {

  return {
    url: `items/${itemId}`,
    method: "DELETE" as const,
  };
}

/**
 * Resolves RTK Query cache tags to invalidate after deleting an item.
 * @param _res void | undefined - Successful mutation result (unused).
 * @param _err unknown - Mutation error (unused).
 * @param itemId number - Deleted item primary key.
 * @returns Array - Cache tags for lists and the deleted item detail.
 */
function deleteItemInvalidatesTags(_res: void | undefined, _err: unknown, itemId: number) {

  return itemMutationTags(itemId);
}

/**
 * Builds the axios request for the next available item number.
 * @returns object - URL and method for GET items/next-number.
 */
function getNextItemNumberQuery() {

  return {
    url: "items/next-number",
    method: "GET" as const,
  };
}

/**
 * Maps the next-number API payload to a numeric item index.
 * @param response object - Raw response with next_item_index.
 * @returns number - Next item number from the server.
 */
function getNextItemNumberTransformResponse(response: { next_item_index: number }): number {

  return response.next_item_index;
}